import {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import ICON_X_LIGHT from '../../../public/static/v4/search/icon-x-light.svg';
import ICON_X from '../../../public/static/v4/search/icon-x.svg';
import Button from '../../common/component/Button';
import useScreenType from '../../common/hook/useScreenType';
import useTheme from '../../common/hook/useTheme';
import {routeBack} from '../../common/util/PageRouter';
import InputV4 from '../../commonSub/component/InputV4';
import {sendSearchEventV4} from '../common';
import {actions} from '../state/action';
import {IViewTypeV4} from '../type';

export interface IProps {
  keyword: string;
  setKeyword: (keyword: string) => void;
  viewType: IViewTypeV4;
  setViewType: (viewType: IViewTypeV4) => void;
  search: (keyword: string) => void;
  totalCount: number;
  onFocus: () => void;
  onClear: () => void;
}

export default function SearchInputV4({
  keyword,
  setKeyword,
  viewType,
  setViewType,
  search,
  totalCount,
  onFocus,
  onClear,
}: IProps) {
  const {isLightMode} = useTheme();
  const {isLarge} = useScreenType();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const onClickCloseBtn = () => {
    sendSearchEventV4({
      viewType,
      actionName: 'back',
      customProps: {
        event_layer: 'header',
        ...(viewType === 'resultView' && {
          search_keyword: keyword,
          search_result_count: totalCount,
        }),
      },
    });

    setKeyword('');
    setViewType('searchView');
    dispatch(actions.resetResult());
    routeBack();
  };

  useEffect(() => {
    isLarge &&
      setTimeout(() => {
        if (viewType === 'resultView') {
          inputRef.current?.blur();
        } else {
          inputRef.current?.focus();
        }
      }, 0);
  }, [viewType, inputRef, isLarge]);

  useEffect(() => {
    const domRect = inputRef.current?.getBoundingClientRect();
    if (domRect) {
      const top = domRect.top + window.scrollY;
      window.scroll({top, behavior: 'smooth'});
    }
  }, [viewType]);

  useEffect(() => {
    const onTouchmove = () => {
      inputRef.current?.blur();
    };

    window.addEventListener('touchmove', onTouchmove);

    return () => {
      window.removeEventListener('touchmove', onTouchmove);
    };
  }, []);

  return (
    <div
      className={cn(
        'flex items-center',
        'fixed top-0 left-1/2 -translate-x-1/2 z-10',
        'pl-11 pr-18 py-10 bg-background',
        'w-full md:w-default-medium-max-width lg:w-default-max-width'
      )}
    >
      <InputV4
        ref={inputRef}
        value={keyword}
        inputVariant="clearButton"
        placeholder={t('search_input_before_enter')}
        className="w-full h-40"
        inputClassName={cn(
          'w-full h-full leading-[1.43] !bg-grey-02',
          'caret-white light:caret-grey-14',
          {
            'text-[14px] font-normal !placeholder-grey-06':
              keyword.length === 0,
            's17-bold-white-black': keyword.length > 0,
          }
        )}
        maxLength={SEARCH_MAX_LENGTH}
        onChange={e => {
          setKeyword(e.currentTarget.value);
        }}
        onKeyUpEnter={() => {
          inputRef.current?.blur();
          search(keyword);
        }}
        onFocus={e => {
          onFocus();
          setTimeout(() => {
            e.target.setSelectionRange?.(keyword.length, keyword.length);
            e.target.scrollLeft = SEARCH_MAX_X_POSITION;
          }, 0);
        }}
        onClear={() => {
          onClear();
          setKeyword('');
        }}
      />
      <Button className="ml-14 w-24 h-24" onClick={onClickCloseBtn}>
        <img
          className="w-24 h-24"
          src={isLightMode ? ICON_X_LIGHT : ICON_X}
          alt="close"
        />
      </Button>
    </div>
  );
}

const SEARCH_MAX_LENGTH = 50;
const SEARCH_MAX_X_POSITION = SEARCH_MAX_LENGTH * 30;
